import { supabase, db } from '@/lib/supabase'
import { supabaseAnonKey, supabaseUrl } from '@/lib/supabaseEnv'
import { isAppApiConfigured, joinAppApiPath, appFetch, appFetchPublic } from '@/lib/appApi'
import { generateUUID } from '@/lib/uuid'
import { funnelSessionStorageKey } from '@/lib/storage/storeScopedKeys'
import { facebookAttributionPayload } from '@/lib/tracking/facebookBrowserIds'
import type {
    AnalyticsStats,
    SalesDataPoint,
    TopProduct,
    RegionData,
    CityPerformance,
} from '@/types/analytics'
import { resolveDeliveryCost, resolveReturnCost } from '@/lib/pricing/resolveShippingCosts'

const SESSION_ID_QUERY = 'session_id'
/** UUID from query string (abandoned-cart email deep links). */
function readSessionIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null
    try {
        const raw = new URL(window.location.href).searchParams.get(SESSION_ID_QUERY)
        if (!raw) return null
        const t = raw.trim()
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) {
            return t
        }
    } catch {
        /* ignore */
    }
    return null
}

/**
 * Funnel session id — scoped per store when `storeId` is set (multi-tenant isolation).
 * Honors `?session_id=` then localStorage.
 */
export function getFunnelSessionId(storeId?: string | null): string {
    const storageKey = funnelSessionStorageKey(storeId)
    const fromUrl = readSessionIdFromUrl()
    if (fromUrl && typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey, fromUrl)
        return fromUrl
    }
    let id = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null
    if (!id) {
        id = generateUUID()
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(storageKey, id)
        }
    }
    return id
}

/** Aligns with `supabase/functions/track-event` `VALID_EVENTS` + store FK type. */
const STORE_ID_UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isAnalyticsStoreUuid(id: string | undefined | null): boolean {
    return typeof id === 'string' && STORE_ID_UUID_RE.test(id.trim())
}

/** Only these use the Edge Function; others use direct `analytics_events` insert (avoids 400 spam). */
const TRACK_EVENT_VIA_EDGE = new Set([
    'page_view',
    'view_content',
    'add_to_cart',
    'checkout_started',
    'checkout_phone_entered',
    'purchase',
    'cta_click',
    'cta_impression',
])

const EVENT_TYPE_TO_META: Partial<Record<string, string>> = {
    page_view: 'PageView',
    view_content: 'ViewContent',
    add_to_cart: 'AddToCart',
    checkout_started: 'InitiateCheckout',
    checkout_phone_entered: 'Lead',
    purchase: 'Purchase',
}

const EDGE_TRACK_EVENT_URL = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/track-event`
const EDGE_ANON_KEY = supabaseAnonKey
const EDGE_RETRY_DELAYS_MS = [250, 700]
const EDGE_PENDING_BUFFER_MAX = 40

type TrackEventRequestBody = {
    store_id: string
    session_id: string
    event_type: string
    payload: Record<string, unknown>
    event?: string
    order_id?: string
    user_id?: string
}

type PendingEdgeEvent = {
    body: TrackEventRequestBody
    attempts: number
}

const pendingEdgeEvents: PendingEdgeEvent[] = []
let listenersReady = false
let flushingPending = false

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function queuePendingEdgeEvent(body: TrackEventRequestBody, attempts: number = 0): void {
    if (pendingEdgeEvents.length >= EDGE_PENDING_BUFFER_MAX) {
        pendingEdgeEvents.shift()
    }
    pendingEdgeEvents.push({ body, attempts })
}

function trySendBeaconTrackEvent(body: TrackEventRequestBody): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false
    let beaconUrl: string
    if (isAppApiConfigured()) {
        beaconUrl = joinAppApiPath('/api/app/public/analytics/track-event')
    } else {
        beaconUrl = EDGE_TRACK_EVENT_URL
        if (!beaconUrl || !beaconUrl.startsWith('http')) return false
    }
    if (!beaconUrl) return false
    try {
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
        return navigator.sendBeacon(beaconUrl, blob)
    } catch {
        return false
    }
}

async function invokeTrackEventEdge(body: TrackEventRequestBody): Promise<string | undefined> {
    if (isAppApiConfigured()) {
        try {
            const res = await fetch(joinAppApiPath('/api/app/public/analytics/track-event'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                keepalive: true,
            })
            if (res.ok) {
                const data = (await res.json().catch(() => null)) as Record<string, unknown> | null
                const eid = typeof data?.event_id === 'string' ? data.event_id.trim() : ''
                return eid || (typeof body.payload.event_id === 'string' ? String(body.payload.event_id) : undefined)
            }
        } catch {
            return undefined
        }
        return undefined
    }
    if (!EDGE_TRACK_EVENT_URL || !EDGE_TRACK_EVENT_URL.startsWith('http') || !EDGE_ANON_KEY) return undefined

    for (let i = 0; i <= EDGE_RETRY_DELAYS_MS.length; i++) {
        try {
            const res = await fetch(EDGE_TRACK_EVENT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    apikey: EDGE_ANON_KEY,
                    Authorization: `Bearer ${EDGE_ANON_KEY}`,
                },
                body: JSON.stringify(body),
                keepalive: true,
            })
            if (res.ok) {
                const data = (await res.json().catch(() => null)) as Record<string, unknown> | null
                const eid = typeof data?.event_id === 'string' ? data.event_id.trim() : ''
                return eid || (typeof body.payload.event_id === 'string' ? body.payload.event_id : undefined)
            }
        } catch {
            // Retry below
        }
        if (i < EDGE_RETRY_DELAYS_MS.length) {
            await sleep(EDGE_RETRY_DELAYS_MS[i])
        }
    }
    return undefined
}

async function flushPendingEdgeEvents(): Promise<void> {
    if (flushingPending || pendingEdgeEvents.length === 0) return
    flushingPending = true
    try {
        while (pendingEdgeEvents.length > 0) {
            const next = pendingEdgeEvents.shift()
            if (!next) break
            const result = await invokeTrackEventEdge(next.body)
            if (result) continue
            if (next.attempts < 2) {
                queuePendingEdgeEvent(next.body, next.attempts + 1)
            }
            break
        }
    } finally {
        flushingPending = false
    }
}

function ensureEdgeFlushListeners(): void {
    if (listenersReady || typeof window === 'undefined') return
    listenersReady = true
    window.addEventListener('online', () => {
        void flushPendingEdgeEvents()
    })
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            void flushPendingEdgeEvents()
        }
    })
}

function stripClientMetaPixel(meta: Record<string, unknown>): { payload: Record<string, unknown> } {
    const payload = { ...meta }
    delete payload.pixel_id
    delete payload.facebook_pixel_id
    return { payload }
}

type LineLike = {
    price?: number | null
    purchase_price?: number | null
    quantity?: number | null
    product_id?: string | null
}

/**
 * Revenue and COGS from persisted order lines (price × qty, purchase_price × qty).
 * Missing or invalid purchase_price skips COGS for that line only (aligned with BFF analytics).
 */
function lineItemRevenueAndCOGS(items: LineLike[]): { revenue: number; cogs: number } {
    let revenue = 0
    let cogs = 0
    for (const item of items) {
        const q = Number(item.quantity) || 0
        const pu = Number(item.price) || 0
        revenue += pu * q
        const rawPp = item.purchase_price
        if (rawPp == null || (typeof rawPp === 'string' && rawPp.trim() === '')) {
            continue
        }
        const pp = Number(rawPp)
        if (!Number.isFinite(pp) || pp < 0) {
            continue
        }
        cogs += pp * q
    }
    return { revenue, cogs }
}

export const analyticsService = {
    // Get overview stats — storeId passed from the hook (via activeStore context)
    /** When `days` is set, orders + visits (page_views) are limited to the last N days so KPIs match charts. */
    async getStats(storeId: string, days?: number): Promise<AnalyticsStats> {
        if (!storeId) throw new Error('storeId requis')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId })
            if (days != null && days > 0) params.set('days', String(days))
            const j = await appFetch<{ ok: true; data: AnalyticsStats }>(`/api/app/analytics/stats?${params.toString()}`)
            return j.data
        }

        const startDate =
            days != null && days > 0
                ? (() => {
                      const d = new Date()
                      d.setDate(d.getDate() - days)
                      return d
                  })()
                : null

        let ordersQuery = db
            .from('orders')
            .select('total, created_at, status, order_items ( product_id, price, purchase_price, quantity )')
            .eq('store_id', storeId)
        if (startDate) {
            ordersQuery = ordersQuery.gte('created_at', startDate.toISOString())
        }
        const { data: orders } = await ordersQuery

        const totalOrders = orders?.length || 0
        const deliveredOrders = orders?.filter(o => o.status === 'delivered') || []
        /** Chiffre d'affaires canon : Σ `orders.total` (commandes livrées uniquement) — aligné avec le dashboard. */
        const totalRevenue = deliveredOrders.reduce((s, o) => s + (Number(o.total) || 0), 0)
        let lineItemRevenueSum = 0
        let totalCOGSDelivered = 0
        for (const o of deliveredOrders) {
            const items = (o.order_items as LineLike[]) || []
            const { revenue, cogs } = lineItemRevenueAndCOGS(items)
            lineItemRevenueSum += revenue
            totalCOGSDelivered += cogs
        }
        const grossProfitOverview = totalRevenue - totalCOGSDelivered
        const profitMarginOverview =
            totalRevenue > 0 ? Math.round((grossProfitOverview / totalRevenue) * 1000) / 10 : 0
        const averageOrderValueOverview =
            deliveredOrders.length > 0
                ? Math.round((totalRevenue / deliveredOrders.length) * 1000) / 1000
                : 0

        // Visits from page_views table (falls back to store.total_visits when no period filter)
        const { data: store } = await db
            .from('stores')
            .select('total_visits')
            .eq('id', storeId)
            .single()

        let totalVisits = store?.total_visits || 0
        let pageViewsQuery = db.from('page_views').select('*', { count: 'exact', head: true }).eq('store_id', storeId)
        if (startDate) {
            pageViewsQuery = pageViewsQuery.gte('created_at', startDate.toISOString())
        }
        const { count: pageViewCount } = await pageViewsQuery
        if (startDate) {
            totalVisits = pageViewCount || 0
        } else if (pageViewCount && pageViewCount > totalVisits) {
            totalVisits = pageViewCount
        }

        const conversionRate = totalVisits > 0 ? (totalOrders / totalVisits) * 100 : 0
        const deliveredOrdersCount = deliveredOrders.length

        return {
            totalVisits,
            totalOrders,
            deliveredOrdersCount,
            conversionRate: Math.round(conversionRate * 10) / 10,
            totalRevenue,
            lineItemRevenue: lineItemRevenueSum,
            totalCOGS: totalCOGSDelivered,
            grossProfit: grossProfitOverview,
            profitMarginPercent: profitMarginOverview,
            averageOrderValue: averageOrderValueOverview,
            visitChange: '',
            orderChange: '',
            conversionChange: '',
            revenueChange: '',
        }
    },

    // Get advanced performance stats — storeId passed from the hook
    async getPerformanceStats(days: number = 7, storeId: string = ''): Promise<AnalyticsStats> {
        if (!storeId) throw new Error('storeId requis')

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, days: String(Math.max(1, days)) })
            const j = await appFetch<{ ok: true; data: AnalyticsStats }>(
                `/api/app/analytics/performance?${params.toString()}`
            )
            return j.data
        }

        const startDate = new Date()
        startDate.setDate(startDate.getDate() - days)

        // 1. Get Orders for Revenue & COGS — scoped to active store
        const { data: orders, error: ordersError } = await db
            .from('orders')
            .select(`id, total, status, order_items ( product_id, price, purchase_price, quantity )`)
            .eq('store_id', storeId)
            .gte('created_at', startDate.toISOString())

        if (ordersError) throw ordersError

        const { data: carrierRow } = await db
            .from('shipping_carriers')
            .select('shipping_cost, return_cost')
            .eq('store_id', storeId)
            .eq('is_active', true)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle()

        const carrier = carrierRow as { shipping_cost?: number | null; return_cost?: number | null } | null

        const productIds = new Set<string>()
        for (const o of orders || []) {
            for (const it of (o.order_items as LineLike[]) || []) {
                if (it.product_id) productIds.add(it.product_id)
            }
        }
        const productMap = new Map<string, { delivery_cost?: number | null; return_cost?: number | null }>()
        if (productIds.size > 0) {
            const { data: prods } = await db
                .from('products')
                .select('id, delivery_cost, return_cost')
                .in('id', [...productIds])
            for (const p of prods || []) {
                const row = p as { id: string; delivery_cost?: number | null; return_cost?: number | null }
                productMap.set(row.id, row)
            }
        }

        // 2. Get Ad Spend — campaigns are user-scoped via RLS (no store_id column needed)
        const { data: campaigns, error: campaignsError } = await db
            .from('campaigns')
            .select('total_cost')
            .eq('store_id', storeId)

        // Ignore campaign error — seller may have no campaigns (organic)
        void campaignsError
        const totalAdSpend = campaigns?.reduce((sum, c) => sum + (Number(c.total_cost) || 0), 0) || 0

        // 3. CA livré = Σ `orders.total` (livrées) ; COGS / coûts internes = lignes produits
        let lineItemRevenue = 0
        let deliveredRevenue = 0
        let totalCOGS = 0
        let internalDeliveryTotal = 0
        let returnInternalCost = 0
        let deliveredCount = 0

        orders?.forEach(order => {
            const items = (order.order_items as LineLike[]) || []

            if (order.status === 'delivered') {
                const { revenue, cogs } = lineItemRevenueAndCOGS(items)
                lineItemRevenue += revenue
                deliveredRevenue += Number(order.total) || 0
                totalCOGS += cogs
                for (const it of items) {
                    const q = Number(it.quantity) || 0
                    const pid = it.product_id
                    if (!pid) continue
                    const p = productMap.get(pid)
                    internalDeliveryTotal += q * resolveDeliveryCost(p ?? {}, carrier ?? {})
                }
                deliveredCount++
            } else if (order.status === 'returned') {
                for (const it of items) {
                    const q = Number(it.quantity) || 0
                    const pid = it.product_id
                    if (!pid) continue
                    const p = productMap.get(pid)
                    returnInternalCost += q * resolveReturnCost(p ?? {}, carrier ?? {})
                }
            }
        })

        const totalRevenue = deliveredRevenue
        const grossProfit = totalRevenue - totalCOGS - internalDeliveryTotal
        const profitMarginPercent =
            totalRevenue > 0 ? Math.round((grossProfit / totalRevenue) * 1000) / 10 : 0
        const averageOrderValue =
            deliveredCount > 0 ? Math.round((totalRevenue / deliveredCount) * 1000) / 1000 : 0

        // Processing fees (like SMS, call center, etc.) - estimated at 500 millimes/order
        const ESTIMATED_PROCESSING_FEE_PER_ORDER = 0.5;
        const totalProcessingFees = (orders?.length || 0) * ESTIMATED_PROCESSING_FEE_PER_ORDER;

        const netProfit =
            grossProfit - (totalAdSpend + returnInternalCost + totalProcessingFees)

        const realRoi =
            totalAdSpend > 0
                ? (netProfit / totalAdSpend) * 100
                : totalRevenue > 0
                  ? (netProfit / totalRevenue) * 100
                  : 0

        // Return rate = returned orders / total non-pending orders
        const returnedCount = orders?.filter(o => o.status === 'returned').length || 0
        const completedOrders = deliveredCount + returnedCount
        const returnRate = completedOrders > 0 ? (returnedCount / completedOrders) * 100 : 0

        return {
            totalVisits: 0, // Not focused here
            totalOrders: orders?.length || 0,
            deliveredOrdersCount: deliveredCount,
            conversionRate: 0,
            totalRevenue,
            lineItemRevenue,
            totalCOGS,
            grossProfit,
            profitMarginPercent,
            averageOrderValue,
            netProfit,
            returnLoss: returnInternalCost,
            realRoi,
            returnRate,
            totalAdSpend,
            visitChange: '',
            orderChange: '',
            conversionChange: '',
            revenueChange: '',
        }
    },

    // Get sales data for charts — storeId passed from hook
    async getSalesData(days: number = 7, storeId: string = ''): Promise<SalesDataPoint[]> {
        if (!storeId) return []

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, days: String(Math.max(1, days)) })
            const j = await appFetch<{ ok: true; data: SalesDataPoint[] }>(
                `/api/app/analytics/sales-data?${params.toString()}`
            )
            return j.data
        }

        const startDate = new Date()
        startDate.setDate(startDate.getDate() - days)

        const { data: orders, error } = await db
            .from('orders')
            .select('total, created_at, status, order_items ( product_id, price, purchase_price, quantity )')
            .eq('store_id', storeId)
            .gte('created_at', startDate.toISOString())
            .order('created_at', { ascending: true })

        if (error) throw error

        const salesByDate: Record<string, { sales: number; orders: number; profit: number }> = {}
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

        for (let i = 0; i < days; i++) {
            const date = new Date()
            date.setDate(date.getDate() - (days - 1 - i))
            const dateKey = date.toISOString().split('T')[0]
            salesByDate[dateKey] = { sales: 0, orders: 0, profit: 0 }
        }

        orders?.forEach(order => {
            const dateKey = order.created_at.split('T')[0]
            if (salesByDate[dateKey] && order.status === 'delivered') {
                const items = (order.order_items as LineLike[]) || []
                const t = Number(order.total) || 0
                const { cogs } = lineItemRevenueAndCOGS(items)
                salesByDate[dateKey].sales += t
                salesByDate[dateKey].profit += t - cogs
                salesByDate[dateKey].orders += 1
            }
        })

        return Object.entries(salesByDate).map(([date, data]) => ({
            date: dayNames[new Date(date).getDay()],
            sales: data.sales,
            orders: data.orders,
            profit: data.profit,
        }))
    },

    // Get top selling products — order_items has no store_id, join via orders
    async getTopProducts(limit: number = 5, storeId: string = '', days?: number): Promise<TopProduct[]> {
        if (!storeId) return []

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({
                storeId,
                limit: String(Math.min(100, Math.max(1, limit))),
            })
            if (days != null && days > 0) params.set('days', String(days))
            const j = await appFetch<{ ok: true; data: TopProduct[] }>(
                `/api/app/analytics/top-products?${params.toString()}`
            )
            return j.data
        }

        let ordersQuery = db
            .from('orders')
            .select('status, order_items ( product_id, product_name, quantity, price, purchase_price )')
            .eq('store_id', storeId)
            .eq('status', 'delivered')
        if (days != null && days > 0) {
            const startDate = new Date()
            startDate.setDate(startDate.getDate() - days)
            ordersQuery = ordersQuery.gte('created_at', startDate.toISOString())
        }

        const { data: orders, error } = await ordersQuery

        if (error) throw error

        const agg: Record<string, { units: number; revenue: number; cogs: number }> = {}
        orders?.forEach(order => {
            const items = (order.order_items as (LineLike & { product_name?: string | null })[]) || []
            items.forEach(item => {
                const name = item.product_name || 'Unknown'
                if (!agg[name]) agg[name] = { units: 0, revenue: 0, cogs: 0 }
                const q = Number(item.quantity) || 0
                agg[name].units += q
                const { revenue, cogs } = lineItemRevenueAndCOGS([item])
                agg[name].revenue += revenue
                agg[name].cogs += cogs
            })
        })

        return Object.entries(agg)
            .map(([name, row]) => ({
                name,
                sales: row.units,
                revenue: Math.round(row.revenue * 1000) / 1000,
                cogs: Math.round(row.cogs * 1000) / 1000,
                profit: Math.round((row.revenue - row.cogs) * 1000) / 1000,
            }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, limit)
    },

    // Get sales by region — storeId passed from hook
    async getSalesByRegion(storeId: string = '', days?: number): Promise<RegionData[]> {
        if (!storeId) return []

        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId })
            if (days != null && days > 0) params.set('days', String(days))
            const j = await appFetch<{ ok: true; data: RegionData[] }>(
                `/api/app/analytics/sales-by-region?${params.toString()}`
            )
            return j.data
        }

        let ordersQuery = db
            .from('orders')
            .select('shipping_city, total')
            .eq('store_id', storeId)
            .eq('status', 'delivered')
        if (days != null && days > 0) {
            const startDate = new Date()
            startDate.setDate(startDate.getDate() - days)
            ordersQuery = ordersQuery.gte('created_at', startDate.toISOString())
        }

        const { data: orders, error } = await ordersQuery

        if (error) throw error

        const regionData: Record<
            string,
            { orders: number; revenue: number }
        > = {}

        orders?.forEach(order => {
            const region = order.shipping_city || 'Autre'
            if (!regionData[region]) {
                regionData[region] = { orders: 0, revenue: 0 }
            }
            regionData[region].orders += 1
            regionData[region].revenue += order.total || 0
        })

        return Object.entries(regionData)
            .map(([region, data]) => ({ region, ...data }))
            .sort((a, b) => b.revenue - a.revenue)
    },

    // Funnel: sessions uniques par étape (analytics_events) — même règle sans API App
    async getFunnelStats(storeId: string, days: number = 7): Promise<Array<{ step_name: string; step_order: number; count_sessions: number; conversion_from_previous: number }>> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Non authentifié')
        if (!storeId) throw new Error('storeId requis')

        const d = Math.max(1, days)
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - d)

        const FUNNEL: Array<{ event_type: string; step_name: string }> = [
            { event_type: 'page_view', step_name: 'Visites (pages vues)' },
            { event_type: 'add_to_cart', step_name: 'Panier' },
            { event_type: 'checkout_started', step_name: 'Checkout' },
            { event_type: 'purchase', step_name: 'Achat' },
        ]

        const { data: rows, error } = await db
            .from('analytics_events')
            .select('session_id, event_type')
            .eq('store_id', storeId)
            .gte('created_at', startDate.toISOString())
            .in(
                'event_type',
                FUNNEL.map(f => f.event_type),
            )

        if (error) throw error

        const byType: Record<string, Set<string>> = Object.fromEntries(
            FUNNEL.map(f => [f.event_type, new Set<string>()]),
        )
        for (const row of rows || []) {
            const sid = typeof row.session_id === 'string' ? row.session_id : ''
            if (!sid || !row.event_type) continue
            if (byType[row.event_type]) {
                byType[row.event_type].add(sid)
            }
        }

        let prev = 0
        return FUNNEL.map((f, idx) => {
            const count = byType[f.event_type]?.size ?? 0
            const conversionFromPrevious =
                idx === 0 ? 100 : prev > 0 ? Math.min(100, Math.round((count / prev) * 1000) / 10) : 0
            prev = count
            return {
                step_name: f.step_name,
                step_order: idx + 1,
                count_sessions: count,
                conversion_from_previous: conversionFromPrevious,
            }
        })
    },

    // Get city performance ROI (P2.2) — uses RPC which handles scoping
    async getCityPerformance(days: number = 30, storeId: string = ''): Promise<CityPerformance[]> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('Non authentifié')
        if (isAppApiConfigured()) {
            const params = new URLSearchParams({ storeId, days: String(days) })
            const j = await appFetch<{ ok: true; data: CityPerformance[] }>(
                `/api/app/analytics/city-performance?${params.toString()}`
            )
            return j.data
        }
        void days
        void storeId
        return []
    },

    // Get total order count for a specific store
    async getOrderCount({ storeId }: { storeId: string }): Promise<number> {
        if (!storeId) return 0;

        try {
            if (isAppApiConfigured()) {
                const j = await appFetch<{ ok: true; count: number }>(
                    `/api/app/analytics/order-count?storeId=${encodeURIComponent(storeId)}`
                )
                return j.count ?? 0
            }
            const { count, error } = await db
                .from('orders')
                .select('*', { count: 'exact', head: true })
                .eq('store_id', storeId);

            if (error) {
                console.error('Error fetching order count:', error);
                return 0;
            }

            return count || 0;
        } catch (err) {
            console.error('Exception in getOrderCount:', err);
            return 0;
        }
    },

    /** One request when App API is configured; otherwise falls back to parallel counts. */
    async getOrderCountsByStoreIds(storeIds: string[]): Promise<Record<string, number>> {
        const ids = [...new Set(storeIds.filter(Boolean))];
        if (ids.length === 0) return {};

        try {
            if (isAppApiConfigured()) {
                const j = await appFetch<{ ok: true; counts: Record<string, number> }>(
                    '/api/app/analytics/order-counts-bulk',
                    { method: 'POST', json: { storeIds: ids } },
                );
                return j.counts && typeof j.counts === 'object' ? j.counts : {};
            }

            const { data, error } = await db.from('orders').select('store_id').in('store_id', ids);
            if (error || !data) {
                const pairs = await Promise.all(ids.map(async (id) => [id, await this.getOrderCount({ storeId: id })] as const));
                return Object.fromEntries(pairs);
            }
            const counts: Record<string, number> = Object.fromEntries(ids.map((id) => [id, 0]));
            for (const row of data) {
                const sid = row.store_id as string;
                if (sid && counts[sid] !== undefined) counts[sid] += 1;
            }
            return counts;
        } catch (e) {
            console.error('getOrderCountsByStoreIds:', e);
            const pairs = await Promise.all(ids.map(async (id) => [id, await this.getOrderCount({ storeId: id })] as const));
            return Object.fromEntries(pairs);
        }
    },

    // Increment store visits (called when store page is viewed)
    async incrementStoreVisits(_store_id: string): Promise<void> {
        return;
    },

    // Track page view (for accurate conversion rate analytics)
    async trackPageView(storeId: string, pagePath: string = '/'): Promise<void> {
        try {
            if (!isAnalyticsStoreUuid(storeId)) {
                if (import.meta.env.DEV) {
                    console.debug('[Analytics] trackPageView skipped: invalid store UUID', { storeId })
                }
                return
            }
            const pvKey = `fastsell_pv_session_${storeId}`
            let sessionId = sessionStorage.getItem(pvKey)
            if (!sessionId) {
                sessionId = generateUUID()
                sessionStorage.setItem(pvKey, sessionId)
            }

            if (isAppApiConfigured()) {
                await appFetchPublic('/api/app/public/analytics/page-view', {
                    method: 'POST',
                    json: {
                        storeId,
                        pagePath,
                        referrer: document.referrer || null,
                        session_id: sessionId,
                    },
                })
            } else {
                await db.from('page_views').insert({
                    store_id: storeId,
                    page_path: pagePath,
                    referrer: document.referrer || null,
                    session_id: sessionId,
                })
            }
        } catch {
            // Silent fail — don't break storefront for visitors
        }
    },

    // Track funnel events through Edge Function only.
    /** Returns canonical `event_id` from Edge when present (Pixel + CAPI dedupe). */
    async trackEvent(storeId: string, sessionId: string, eventName: string, metadata: any = {}): Promise<string | undefined> {
        try {
            const ev = typeof eventName === 'string' ? eventName.trim() : ''
            const sid = typeof storeId === 'string' ? storeId.trim() : ''
            const sess = typeof sessionId === 'string' ? sessionId.trim() : ''

            if (!sid || !sess || !ev) {
                if (import.meta.env.DEV) {
                    console.debug('[Analytics] trackEvent skipped: missing storeId, sessionId, or event', {
                        storeId: sid,
                        sessionId: sess,
                        eventName: ev,
                    })
                }
                return undefined
            }

            if (!isAnalyticsStoreUuid(sid)) {
                if (import.meta.env.DEV) {
                    console.warn('[Analytics] trackEvent skipped: invalid store UUID', { storeId: sid })
                }
                return undefined
            }

            const raw =
                metadata && typeof metadata === 'object' && !Array.isArray(metadata)
                    ? (metadata as Record<string, unknown>)
                    : {}
            /** Refresh fbp/fbc/fbclid so CAPI always sees latest attribution. */
            const base = { ...raw, ...facebookAttributionPayload() }
            const { payload } = stripClientMetaPixel(base)

            const fallbackEventId =
                typeof payload.event_id === 'string' && payload.event_id.trim() ? payload.event_id.trim() : undefined

            const viaEdge = TRACK_EVENT_VIA_EDGE.has(ev)
            if (import.meta.env.DEV) {
                console.debug('[Analytics] track-event outgoing', {
                    store_id: sid,
                    session_id: sess,
                    event_type: ev,
                    ...(viaEdge && EVENT_TYPE_TO_META[ev] ? { event: EVENT_TYPE_TO_META[ev] } : {}),
                    via_edge: viaEdge,
                    payload_keys: Object.keys(payload),
                })
            }

            if (viaEdge) {
                const body: Record<string, unknown> = {
                    store_id: sid,
                    session_id: sess,
                    event_type: ev,
                    payload,
                }
                const metaAlias = EVENT_TYPE_TO_META[ev]
                if (metaAlias) body.event = metaAlias

                const bodyTyped = body as TrackEventRequestBody
                ensureEdgeFlushListeners()
                await flushPendingEdgeEvents()

                // Critical events get an unload-safe beacon attempt first.
                if (
                    typeof document !== 'undefined' &&
                    (ev === 'checkout_phone_entered' || ev === 'checkout_started') &&
                    document.visibilityState === 'hidden'
                ) {
                    const beaconQueued = trySendBeaconTrackEvent(bodyTyped)
                    if (beaconQueued) {
                        queuePendingEdgeEvent(bodyTyped, 1)
                        return fallbackEventId
                    }
                }

                const rid = await invokeTrackEventEdge(bodyTyped)
                if (rid) return rid || fallbackEventId

                queuePendingEdgeEvent(bodyTyped, 1)
                if (import.meta.env.DEV) {
                    console.warn('[Analytics] track-event failed, queued in memory buffer', {
                        event: ev,
                        store_id: sid,
                    })
                }
            }

            return fallbackEventId
        } catch (err) {
            if (import.meta.env.DEV) {
                console.warn('[Analytics] trackEvent error:', err)
            }
            return undefined
        }
    },

    // Update checkout session with contact info (persists for abandoned-checkout email)
    async updateCheckoutSession(storeId: string, sessionId: string, data: { phone?: string, email?: string }): Promise<void> {
        try {
            if (import.meta.env.DEV) {
                console.log(`[Analytics] Update Session: ${sessionId}`, { storeId, data });
            }
            if (isAppApiConfigured()) {
                const { upsertCheckoutSession } = await import('@/services/checkoutSession')
                await upsertCheckoutSession({
                    storeId,
                    sessionId,
                    email: data.email ?? null,
                    phone: data.phone ?? null,
                })
                return
            }
            const patch: Record<string, unknown> = {
                last_active_at: new Date().toISOString(),
            }
            if (data.email !== undefined) patch.email = data.email
            if (data.phone !== undefined) patch.phone = data.phone

            const { data: updated, error: updateErr } = await db
                .from('checkout_sessions')
                .update(patch)
                .eq('session_id', sessionId)
                .eq('store_id', storeId)
                .select('session_id')
                .maybeSingle()

            if (!updateErr && updated?.session_id) return

            const { upsertCheckoutSession } = await import('@/services/checkoutSession')
            await upsertCheckoutSession({
                storeId,
                sessionId,
                email: data.email ?? null,
                phone: data.phone ?? null,
            })
        } catch (error) {
            console.error('[Analytics] updateCheckoutSession error:', error);
        }
    }
}
